"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Link,
  Card,
  CardBody,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import OTPInput from "./OTPInput";

const PasswordResetOTP: React.FC = () => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const { verifyOtp, resetPassword } = useAuth();

  useEffect(() => {
    if (!email) {
      navigate("/password-reset");
    }
  }, [email, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { error: verifyError } = await verifyOtp(email, otp, "recovery");

      if (verifyError) {
        setError(
          verifyError.message || "Invalid verification code. Please try again."
        );
        return;
      }

      // Navigate to set new password page
      navigate("/set-new-password", { state: { email, otp } });
    } catch (err: any) {
      console.log("[v0] OTP verification error:", err);
      setError("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMessage("");
    setError("");

    try {
      const { error: resendError } = await resetPassword(email);

      if (resendError) {
        setError(
          resendError.message || "Failed to resend code. Please try again."
        );
        return;
      }

      setResendMessage("Verification code sent!");
    } catch (err: any) {
      console.log("[v0] Resend OTP error:", err);
      setError("Failed to resend code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="md">
        <Card>
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <VStack spacing={2} textAlign="center">
                <Box
                  as="img"
                  src={process.env.REACT_APP_MAIN_LOGO_URL}
                  alt="App Logo"
                  h="100px"
                  mx="auto"
                  mb={2}
                />
                <Text color="gray.600">Enter verification code</Text>
                <Text fontSize="sm" color="gray.500">
                  We sent a 6-digit code to {email}
                </Text>
              </VStack>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <OTPInput value={otp} onChange={setOtp} />

                  {error && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {error}
                    </Alert>
                  )}

                  {resendMessage && (
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      {resendMessage}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="brandGradient"
                    size="lg"
                    width="full"
                    isLoading={loading}
                    loadingText="Verifying..."
                    isDisabled={otp.length !== 6}
                  >
                    Verify Code
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResend}
                    isLoading={resendLoading}
                    loadingText="Sending..."
                    color="gray.600"
                    _hover={{ color: "primary.500" }}
                  >
                    Didn't receive the code? Resend
                  </Button>
                </VStack>
              </form>

              {/* Back to Password Reset */}
              <Text textAlign="center" fontSize="sm" color="gray.600">
                <Link
                  as={RouterLink}
                  to="/password-reset"
                  color="primary.500"
                  fontWeight="medium"
                >
                  ← Back to password reset
                </Link>
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default PasswordResetOTP;
